/** @jsxImportSource @emotion/react */
import React from "react";
import {
  MaterialUsecase,
  useMaterialDispatch,
  useMaterialState,
} from "../../../usecases/Material";
import { TenantUsecase, useTenantDispatch } from "../../../usecases/Tenant";
import { useAuthState } from "../../../usecases/Auth";
import { useSettingState } from "../../../usecases/Setting";
import { GetListParams } from "../../../services/Material/material.repository.d";
import { Images } from "../../../common/images";
import {
  DataTable,
  Toolbox,
  useColumnVisibility,
  useInlineEdit,
  ColumnConfig,
  DisplayColumns,
  ThumbnailSize,
} from "../../../components/DataTable";
import { LayoutContent } from "../../../components/LayoutContent";

const MATERIAL_COLUMN_CONFIGS: ColumnConfig[] = [
  { key: "materialThumbnailUrl", title: "Thumbnail", type: "thumbnail" },
  { key: "materialKey", title: "Key", type: "plain", sortable: true },
  { key: "name", title: "Name", type: "text", sortable: true, editable: true },
  {
    key: "materialCategories",
    title: "Category",
    type: "arrayString",
    sortable: true,
    editable: true,
    filterable: true,
  },
  {
    key: "materialUnitPrice",
    title: "Unit Price",
    type: "number",
    sortable: true,
    editable: true,
  },
  {
    key: "materialRooms",
    title: "Room",
    type: "arrayString",
    sortable: true,
    filterable: true,
  },
  {
    key: "materialSurfaces",
    title: "Surface",
    type: "arrayString",
    sortable: true,
    filterable: true,
  },
  {
    key: "materialManufacturer",
    title: "Manufacturer",
    type: "text",
    sortable: true,
    editable: true,
  },
  {
    key: "materialInstanceFileName",
    title: "File Name",
    type: "text",
    sortable: true,
    editable: true,
  },
  { key: "homeNames", title: "Homes", type: "arrayString", sortable: true },
  {
    key: "updatedByFullName",
    title: "Modified By",
    type: "text",
    sortable: true,
  },
  { key: "updatedAt", title: "Date Modified", type: "date", sortable: true },
  { key: "isActive", title: "Status", type: "status", editable: true },
];

const MaterialComponent = () => {
  const materialState = useMaterialState();
  const materialDispatch = useMaterialDispatch();
  const tenantDispatch = useTenantDispatch();
  const authState = useAuthState();
  const settingState = useSettingState();

  const [listParams, setListParams] = React.useState<GetListParams>({});

  // Sync tenantId from settingState into listParams
  React.useEffect(() => {
    if (settingState) {
      if (listParams.tenantId !== settingState.tenantInfo?.tenantId) {
        setListParams((prev) => ({
          ...prev,
          tenantId: settingState.tenantInfo?.tenantId,
        }));
      }
    }
  }, [listParams, settingState]);

  React.useEffect(() => {
    async function asyncLoadData() {
      if (materialDispatch) {
        const materialUsecase = new MaterialUsecase(materialDispatch);
        await materialUsecase.getList(listParams);
      }
      if (tenantDispatch) {
        const tenantUsecase = new TenantUsecase(tenantDispatch);
        await tenantUsecase.getList({});
      }
    }
    asyncLoadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialDispatch, tenantDispatch, listParams]);

  const handleSearch = React.useCallback((keyword: string) => {
    setListParams((prev) => ({ ...prev, keyword }));
  }, []);

  const handleSave = React.useCallback(
    async (items: Array<Record<string, unknown>>) => {
      if (materialDispatch) {
        const materialUsecase = new MaterialUsecase(materialDispatch);
        await materialUsecase.updateList({
          userId: authState.userInfo?.username,
          items,
        });
      }
    },
    [authState.userInfo?.username, materialDispatch],
  );

  const materialUsecase = React.useMemo(
    () =>
      materialDispatch ? new MaterialUsecase(materialDispatch) : undefined,
    [materialDispatch],
  );

  const getDisplayColumns = React.useCallback(
    (): DisplayColumns =>
      (materialUsecase?.getDisplayColumns() ?? {}) as DisplayColumns,
    [materialUsecase],
  );

  const setDisplayColumns = React.useCallback(
    (cols: DisplayColumns) => materialUsecase?.setDisplayColumns(cols as never),
    [materialUsecase],
  );

  const [thumbnailSize, setThumbnailSize] =
    React.useState<ThumbnailSize>("medium");

  const visibility = useColumnVisibility(
    MATERIAL_COLUMN_CONFIGS,
    getDisplayColumns,
    setDisplayColumns,
  );

  const edit = useInlineEdit(
    MATERIAL_COLUMN_CONFIGS,
    materialState.data,
    "materialKey",
    handleSave,
  );

  return (
    <LayoutContent
      title="MATERIALS"
      iconPath={Images.PALETTE_BLACK}
      toolbar={
        <Toolbox>
          <Toolbox.Search onSearch={handleSearch} placeholder="Find..." />
          <Toolbox.ColumnToggle {...visibility} />
          <Toolbox.ThumbnailResize
            value={thumbnailSize}
            onChange={setThumbnailSize}
          />
          <Toolbox.Spacer />
          <Toolbox.EditActions
            isEditing={edit.isEditing}
            onEdit={edit.onButtonEditClick}
            onCancel={edit.onButtonCancelClick}
          />
        </Toolbox>
      }
    >
      <DataTable
        rowKey="materialKey"
        dataSource={materialState.data}
        columnConfigs={MATERIAL_COLUMN_CONFIGS}
        columnToggle
        editable
        pagination
        thumbnailSize={thumbnailSize}
        visibility={visibility}
        edit={edit}
      />
    </LayoutContent>
  );
};

export const Material = React.memo(MaterialComponent);
